<template>
  <div class="main_Container">
    <section class="p-4">
      <div class="container-fluid">
        <div class="card">
          <div class="card-header">
            <h3 class="card-header-title">تصحيح الاسئلة المقالية</h3>
          </div>

          <div class="card-body">
            <!-- Loading -->
            <div v-if="isLoading" class="data__loading text-center py-5">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else-if="!result" class="no-data">
              <i class="icon fas fa-exclamation-circle"></i>
              <p>لا يوجد اجابات لهذا الطالب</p>
            </div>

            <template v-else>
              <!-- Summary -->
              <div class="row mb-4">
                <div class="col-lg-3">
                  <div class="form-group">
                    <label>اسم الطالب</label>
                    <input
                      type="text"
                      class="form-control mt-2"
                      :value="result.user?.user_name || result.name || ''"
                      readonly
                    />
                  </div>
                </div>
                <div class="col-lg-3">
                  <div class="form-group">
                    <label>درجة الاختيارات</label>
                    <input
                      type="text"
                      class="form-control mt-2"
                      :value="mcqScore"
                      readonly
                    />
                  </div>
                </div>
                <div class="col-lg-3">
                  <div class="form-group">
                    <label>درجة المقالي</label>
                    <input
                      type="text"
                      class="form-control mt-2"
                      :value="awardedEssayDegree"
                      readonly
                    />
                  </div>
                </div>
                <div class="col-lg-3">
                  <div class="form-group">
                    <label>الدرجة النهائية</label>
                    <input
                      type="text"
                      class="form-control mt-2"
                      :value="`${totalScore} من ${examDegree}`"
                      readonly
                    />
                  </div>
                </div>
              </div>

              <!-- Nothing to grade -->
              <div v-if="essayAnswers.length === 0" class="no-data mt-0">
                <i class="icon fas fa-check-circle"></i>
                <p>هذا الاختبار لا يحتوي على اسئلة مقالية</p>
              </div>

              <!-- Essay answers -->
              <div
                v-for="item in essayAnswers"
                :key="item.index"
                class="card mb-3"
              >
                <div class="card-body">
                  <div
                    class="d-flex justify-content-between align-items-start mb-2"
                  >
                    <h5 class="mb-0">
                      سؤال {{ item.index + 1 }}
                    </h5>
                    <span class="badge bg-secondary">
                      من {{ item.maxDegree }} درجة
                    </span>
                  </div>

                  <div class="mb-3">
                    <label class="text-muted">نص السؤال</label>
                    <div class="mt-1" v-html="item.questionTitle"></div>
                  </div>

                  <div class="mb-3" v-if="item.modelAnswer">
                    <label class="text-muted">الاجابة النموذجية</label>
                    <p class="mt-1 mb-0">{{ item.modelAnswer }}</p>
                  </div>

                  <div class="mb-3">
                    <label class="text-muted">اجابة الطالب</label>
                    <p class="mt-1 mb-0" v-if="item.studentAnswer">
                      {{ item.studentAnswer }}
                    </p>
                    <p class="mt-1 mb-0 text-danger" v-else>
                      لم يجب الطالب على هذا السؤال
                    </p>
                  </div>

                  <div class="form-group" style="max-width: 220px">
                    <label :for="`degree-${item.index}`">الدرجة</label>
                    <input
                      type="number"
                      class="form-control mt-2"
                      :id="`degree-${item.index}`"
                      min="0"
                      step="0.5"
                      :max="item.maxDegree"
                      v-model.number="marks[item.index]"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="card-footer" v-if="result && essayAnswers.length > 0">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isSaving"
              @click="saveGrades"
            >
              {{ isSaving ? "جاري الحفظ..." : "حفظ الدرجات" }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Swal from "sweetalert2";

import {
  QUESTION_TYPE,
  RESULT_STATUS,
  DEFAULT_QUESTION_DEGREE,
} from "@/constants/exam";

export default {
  name: "AdminExamGradeStudent",
  data() {
    return {
      examId: this.$route.params.id,
      studentId: this.$route.params.studentId,
      isLoading: true,
      isSaving: false,
      exam: null,
      result: null,
      // question index -> marks awarded
      marks: {},
    };
  },
  computed: {
    examDegree() {
      return Number(this.exam?.degree) || 0;
    },

    // MCQ marks the app computed at submit time. A pending result stores the
    // MCQ subtotal; older results stored -1 for any exam holding an essay.
    mcqScore() {
      const score = Number(this.result?.result ?? this.result?.score);
      return Number.isFinite(score) && score > 0 ? score : 0;
    },

    // Pairs each essay question in the exam with the student's answer.
    essayAnswers() {
      const questions = this.exam?.questions ?? [];
      const answers = this.result?.questions ?? [];

      return questions
        .map((question, index) => ({ question, index }))
        .filter(
          ({ question }) =>
            Number(question.questionType) === QUESTION_TYPE.essay
        )
        .map(({ question, index }) => {
          const answer = answers[index] ?? {};
          return {
            index,
            questionTitle: question.title ?? "",
            modelAnswer: question.answers?.[0]?.title ?? "",
            studentAnswer: answer.answer ?? "",
            maxDegree: Number(question.degree) || DEFAULT_QUESTION_DEGREE,
          };
        });
    },

    awardedEssayDegree() {
      return this.essayAnswers.reduce((total, item) => {
        const mark = Number(this.marks[item.index]);
        return total + (Number.isFinite(mark) ? mark : 0);
      }, 0);
    },

    totalScore() {
      return this.mcqScore + this.awardedEssayDegree;
    },
  },
  methods: {
    ...mapActions("examsModule", [
      "getExamById",
      "getStudentResult",
      "gradeStudentResult",
    ]),

    async load() {
      this.isLoading = true;
      try {
        const [exam, result] = await Promise.all([
          this.getExamById(this.examId),
          this.getStudentResult({
            examId: this.examId,
            studentId: this.studentId,
          }),
        ]);

        this.exam = exam;
        this.result = result;

        // Pre-fill with any marks already awarded.
        const marks = {};
        (result?.questions ?? []).forEach((answer, index) => {
          const awarded = Number(answer?.degree_awarded);
          if (Number.isFinite(awarded)) marks[index] = awarded;
        });
        this.marks = marks;
      } catch (error) {
        console.error("Could not load the student's answers", error);
        this.showToast("error", "لم يتم تحميل اجابات الطالب");
      } finally {
        this.isLoading = false;
      }
    },

    async saveGrades() {
      // Refuse marks above what the question is worth.
      const invalid = this.essayAnswers.find((item) => {
        const mark = Number(this.marks[item.index]);
        return Number.isFinite(mark) && mark > item.maxDegree;
      });
      if (invalid) {
        this.showToast(
          "error",
          `درجة السؤال ${invalid.index + 1} أكبر من ${invalid.maxDegree}`
        );
        return;
      }

      const ungraded = this.essayAnswers.some(
        (item) => !Number.isFinite(Number(this.marks[item.index]))
      );

      const questions = (this.result.questions ?? []).map((answer, index) => {
        const mark = Number(this.marks[index]);
        if (!Number.isFinite(mark)) return answer;
        return { ...answer, degree_awarded: mark };
      });

      this.isSaving = true;
      try {
        await this.gradeStudentResult({
          examId: this.examId,
          studentId: this.studentId,
          questions,
          essayDegree: this.awardedEssayDegree,
          status: ungraded ? RESULT_STATUS.pending : RESULT_STATUS.graded,
        });

        this.showToast(
          "success",
          ungraded ? "تم حفظ الدرجات، باقي اسئلة بدون تصحيح" : "تم التصحيح بنجاح"
        );
        if (!ungraded) this.$router.go(-1);
      } catch (error) {
        console.error("Could not save the grades", error);
        this.showToast("error", "لم يتم حفظ الدرجات، حاول مرة اخرى");
      } finally {
        this.isSaving = false;
      }
    },

    showToast(icon, title) {
      Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      }).fire({ icon, title });
    },
  },
  async created() {
    await this.load();
  },
};
</script>

<style lang="scss" scoped></style>
